<script lang="ts" setup>
import { QCard } from 'quasar'
import { computed } from 'vue'
import { Color, ColorSemantic } from '@previo/codebooks'
import { usePrevioDS } from '../../composables/helpers'
import { PSpacePrimitive, PSpaceSemantic } from '../space/types'
import { PCardRound, PCardSize } from './types'

type Props = {
  maMobile?: PCardSize | PSpaceSemantic | PSpacePrimitive
  paMobile?: PCardSize | PSpaceSemantic | PSpacePrimitive
  maLaptop?: PCardSize | PSpaceSemantic | PSpacePrimitive
  paLaptop?: PCardSize | PSpaceSemantic | PSpacePrimitive
  maDesktop?: PCardSize | PSpaceSemantic | PSpacePrimitive
  paDesktop?: PCardSize | PSpaceSemantic | PSpacePrimitive
  color?: null | Color | ColorSemantic
  round?: PCardRound
  roundTopStart?: PCardRound
  roundTopEnd?: PCardRound
  roundBottomStart?: PCardRound
  roundBottomEnd?: PCardRound
  showBorder?: boolean
  disable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  maMobile: PCardSize.ZERO,
  paMobile: PCardSize.ZERO,
  color: Color.TRANSPARENT,
  round: PCardRound.ZERO,
  showBorder: false,
  disable: false,
})

const ds = usePrevioDS()

const roundClass = computed(() => {
  return `
    ds-card-round-top-start-${props.roundTopStart || props.round}
    ds-card-round-top-end-${props.roundTopEnd || props.round}
    ds-card-round-bottom-start-${props.roundBottomStart || props.round}
    ds-card-round-bottom-end-${props.roundBottomEnd || props.round}
  `
})

const maSize = computed<PCardSize | PSpaceSemantic | PSpacePrimitive>(() => {
  let size = props.maMobile

  if (ds.screen().isLaptopAndLarge && props.maLaptop) {
    size = props.maLaptop
  }

  if (ds.screen().isDesktop && props.maDesktop) {
    size = props.maDesktop
  }

  return size
})

const paSize = computed<PCardSize | PSpaceSemantic | PSpacePrimitive>(() => {
  let size = props.paMobile

  if (ds.screen().isLaptopAndLarge && props.paLaptop) {
    size = props.paLaptop
  }

  if (ds.screen().isDesktop && props.paDesktop) {
    size = props.paDesktop
  }

  return size
})
</script>

<template>
  <q-card
    :class="[
      `ds-card ds-card-ma-${maSize} ds-card-pa-${paSize} ${roundClass} bg-${color}`,
      { 'ds-card--border': showBorder, 'ds-card--disable': disable },
    ]"
    flat
  >
    <slot />
  </q-card>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/brand.module';

.ds-card {
  &--border {
    position: relative;

    // Overlay instead of a real border: it does not shift the content by 1px and, unlike
    // an inset shadow, stays visible above children that paint their own background
    // edge to edge (scheduler, data grids, ...)
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      border: 1px solid getColor('grey200');
      border-radius: inherit;
      pointer-events: none;
    }
  }

  &--disable {
    opacity: 0.5;
    pointer-events: none;
    user-select: none;
  }
}

@each $name, $value in $radius {
  .ds-card-round-top-start-#{$name} {
    border-top-left-radius: #{map-get(
        $value,
        'mobile'
      )} !important; // Important need for override native Quasar styles
  }

  .ds-card-round-top-end-#{$name} {
    border-top-right-radius: #{map-get(
        $value,
        'mobile'
      )} !important; // Important need for override native Quasar styles
  }

  .ds-card-round-bottom-start-#{$name} {
    border-bottom-left-radius: #{map-get(
        $value,
        'mobile'
      )} !important; // Important need for override native Quasar styles
  }

  .ds-card-round-bottom-end-#{$name} {
    border-bottom-right-radius: #{map-get(
        $value,
        'mobile'
      )} !important; // Important need for override native Quasar styles
  }
}

@each $name, $padding in $spaces {
  .ds-card-pa-#{$name} {
    @include mobile {
      padding: map-get($padding, 'mobile');
    }

    @include laptop {
      padding: map-get($padding, 'laptop');
    }

    @include desktop {
      padding: map-get($padding, 'desktop');
    }
  }
}

@each $name, $margin in $spaces {
  .ds-card-ma-#{$name} {
    @include mobile {
      margin: map-get($margin, 'mobile');
    }

    @include laptop {
      margin: map-get($margin, 'laptop');
    }

    @include desktop {
      margin: map-get($margin, 'desktop');
    }
  }
}

@each $key, $space in $semantic-spaces {
  .ds-card-pa-#{$key} {
    @include mobile {
      padding: var(--space-#{$key}-mobile);
    }

    @include laptop {
      padding: var(--space-#{$key}-laptop);
    }

    @include desktop {
      padding: var(--space-#{$key}-desktop);
    }
  }

  .ds-card-ma-#{$key} {
    @include mobile {
      margin: var(--space-#{$key}-mobile);
    }

    @include laptop {
      margin: var(--space-#{$key}-laptop);
    }

    @include desktop {
      margin: var(--space-#{$key}-desktop);
    }
  }
}

@each $key, $value in $space-primitives {
  .ds-card-pa-#{$key} {
    padding: var(--#{$key});
  }

  .ds-card-ma-#{$key} {
    margin: var(--#{$key});
  }
}
</style>
